import {
	FormControl,
	InputLabel,
	MenuItem,
	Typography,
	Select,
} from '@mui/material';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
	caps: {
		textTransform: 'uppercase',
	},
	error: {
		fontSize: '.75rem',
		color: theme.palette.danger.main,
	},
}));

/**
 * Provides a component allowing file export/download via selectable formats.
 *
 * @param {Object} props
 * @param {{ key: string | number, value: string }[]} props.options - Available export formats
 * @param {(format: string) => Promise<void>} props.onDownload - Triggered when a format is selected
 * @param {string} props.label - Label for the select input
 * @param {string} [props.dataTestId] - Optional test id for automation
 * @param {boolean} props.isLoading - Disables the component while loading
 */
export function FileExportSelector({
	options,
	onDownload,
	label,
	dataTestId,
	isLoading,
}) {
	const { t } = useTranslation();
	const classes = useStyles();

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [selected, setSelected] = useState('');

	const flashError = (msg) => {
		setError(msg);
		setTimeout(() => setError(''), 5000);
	};

	const handleChange = async (event) => {
		const format = event.target.value;

		setSelected(format);
		setLoading(true);
		setError('');

		try {
			await onDownload(format);
		} catch (err) {
			flashError(err.message || t('downloadError'));
		} finally {
			setLoading(false);
		}
	};

	return (
		<FormControl fullWidth disabled={loading || isLoading} size="small">
			<InputLabel
				id="select-small-label"
				sx={{
					ml: '-.5rem',
				}}
			>
				{t('export')}
			</InputLabel>
			<Select
				data-testid={dataTestId || 'data_export'}
				defaultValue=""
				value={isLoading ? selected : ''}
				label={label}
				inputProps={{ 'aria-label': 'select-export-type' }}
				onChange={handleChange}
			>
				{options.map((opt) => (
					<MenuItem key={opt.key} value={opt.key}>
						{opt.value}
					</MenuItem>
				))}
			</Select>

			{error && <Typography className={classes.error}>{error}</Typography>}
		</FormControl>
	);
}

FileExportSelector.propTypes = {
	options: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			value: PropTypes.string.isRequired,
		})
	).isRequired,
	onDownload: PropTypes.func.isRequired,
	label: PropTypes.string.isRequired,
	dataTestId: PropTypes.string,
	isLoading: PropTypes.bool.isRequired,
};

export default FileExportSelector;
